import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import styles from "./WcpmScoreCounter.module.css";

const FPS = 24;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Olympic-style WCPM reveal — floating panel
 */
export default function WcpmScoreCounter({ target, passingWcpm, onComplete }) {
  const [display, setDisplay] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const final = Math.min(999, Math.max(0, Number(target) || 0));
    const naturalSec = (final + 1) / FPS;
    const durationSec = Math.min(5, Math.max(2.25, naturalSec));
    const totalFrames = Math.max(1, Math.ceil(durationSec * FPS));
    let frame = 0;
    const id = setInterval(() => {
      frame += 1;
      const linearT = Math.min(1, frame / totalFrames);
      const easedT = easeOutCubic(linearT);
      const next = Math.round(easedT * final);
      setDisplay(next);
      if (frame >= totalFrames) {
        clearInterval(id);
        setDisplay(final);
        onCompleteRef.current?.();
      }
    }, 1000 / FPS);
    return () => clearInterval(id);
  }, [target]);

  const passLine = Number(passingWcpm) || 0;
  const passed = display >= passLine;
  const text = String(display);

  return (
    <motion.div
      className={styles.panel}
      initial={{ opacity: 0, y: 20, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      role="status"
      aria-live="polite"
      aria-label={`Words correct per minute ${display}`}
    >
      <p className={styles.label}>Words correct per minute</p>
      <motion.div
        className={styles.numberRow}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.08, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className={styles.digits} data-passed={passed ? "true" : "false"}>
          {text}
        </span>
        <span className={styles.unit}>WCPM</span>
      </motion.div>
    </motion.div>
  );
}
